import { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useGlobalContext } from "../GlobalContext";

const useSettlement = () => {
  const { baseurl, token, AdminId, setIsUnauthorised } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  // ===== DRIVER ANALYTICS =====
  const fetchDriverAnalytics = async (
    driverId,
    fromDate,
    toDate,
    setAnalytics,
  ) => {
    if (!driverId) return;

    setLoading(true);
    try {
      const payload = {
        adminId: AdminId,
        driverCode: driverId,
        fromDate,
        toDate,
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v2.0/getDriverAnalytics`,
        payload,
        { headers: { Authorization: `Basic ${token}` } },
      );

      setAnalytics(res.data?.data || null);
    } catch (err) {
      console.error("Analytics API Error:", err);
      if (err?.response?.status === 401) setIsUnauthorised(true);
    } finally {
      setLoading(false);
    }
  };

  // ===== SETTLEMENT REPORT =====
  const fetchSettlementReport = async (
    driverId,
    fromDate,
    toDate,
    setSettlementRows,
  ) => {
    if (!driverId) return;

    setSettlementLoading(true);
    try {
      const payload = {
        adminId: AdminId,
        driverCode: driverId,
        fromDate,
        toDate,
      };

      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/getSettlementReport`,
        payload,
        { headers: { Authorization: `Basic ${token}` } },
      );

      setSettlementRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Settlement API Error:", err);
      if (err?.response?.status === 401) setIsUnauthorised(true);
    } finally {
      setSettlementLoading(false);
    }
  };

  // ===== SETTLE AMOUNT =====
  const handleSettleAmount = async (
    driverId,
    fromDate,
    toDate,
    payAmount,
    bonusAmount,
    setAmountPaid,
    refreshAnalytics,
    refreshSettlement,
  ) => {
    if (!payAmount && !bonusAmount) {
      toast.error("Please enter amount or bonus");
      return;
    }

    const loadingToastId = toast.loading("Settling amount...");

    try {
      setPaying(true);

      const payload = {
        adminId: AdminId,
        driverCode: driverId,
        fromDate,
        toDate,
        amount: Number(payAmount || 0),
        bonusAmount: Number(bonusAmount || 0),
      };

      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/settleDriverAmount`,
        payload,
        { headers: { Authorization: `Basic ${token}` } },
      );

      if (res.data?.responseCode === "00") {
        const paid = Number(res.data?.data?.amountPaid || 0);
        setAmountPaid(paid);

        // refresh data
        await refreshSettlement();
        await refreshAnalytics();

        toast.success("Amount settled successfully", {
          id: loadingToastId,
        });
      } else {
        toast.error(res.data?.responseMessage || "Failed to settle amount", {
          id: loadingToastId,
        });
      }
    } catch (err) {
      console.error("Settle Amount Error", err);

      if (err?.response?.status === 401) {
        setIsUnauthorised(true);
      }

      toast.error("Something went wrong while settling amount", {
        id: loadingToastId,
      });
    } finally {
      setPaying(false);
    }
  };

  return {
    fetchDriverAnalytics,
    fetchSettlementReport,
    handleSettleAmount,
    loading,
    settlementLoading,
    paying,
  };
};

export default useSettlement;
